import { notification, NotificationArgsProps } from 'antd';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from 'react';

import {
  ENotificationSeverity,
  type ShowNotificationMessage,
} from '../admin-types';
import Socket from './socket';

export type NotificationPlacement = NotificationArgsProps['placement'];

export type OpenNotificationType = (
  placement: NotificationPlacement,
  message: ShowNotificationMessage,
) => void;

const DEFAULT_NOTIFICATION_PLACEMENT: NotificationPlacement = 'topRight';

export const notificationContext = createContext({
  openNotification: (
    _placement: NotificationPlacement,

    _message: ShowNotificationMessage,
  ): void => {},
});

function Notification({
  children,
  socket = undefined,
}: {
  children: React.ReactElement;
  socket?: Socket;
}): React.ReactElement {
  const [api, contextHolder] = notification.useNotification();

  const openNotification: OpenNotificationType = useCallback(
    (
      placement: NotificationPlacement,
      message: ShowNotificationMessage,
    ): void => {
      const fn =
        message.severity === ENotificationSeverity.ERROR
          ? api.error
          : message.severity === ENotificationSeverity.WARNING
            ? api.warning
            : message.severity === ENotificationSeverity.SUCCESS
              ? api.success
              : api.info;

      fn({
        title: message.title,
        description: message.description,
        placement,
      });
    },
    [api.error, api.info, api.success, api.warning],
  );

  useEffect((): (() => void) => {
    if (socket) {
      socket.subscribeEventShowNotification(
        (message: ShowNotificationMessage): void => {
          openNotification(DEFAULT_NOTIFICATION_PLACEMENT, message);
        },
      );
    }

    return (): void => {
      if (socket) {
        socket.unsubscribeEventShowNotification();
      }
    };
  }, [socket, openNotification]);

  useEffect((): void => {
    if (socket) {
      socket.setOpenNotification(openNotification);
    }
  }, [socket, openNotification]);

  return (
    <notificationContext.Provider value={{ openNotification }}>
      {contextHolder}
      {children}
    </notificationContext.Provider>
  );
}

export default Notification;

export type NotifyType = (
  severity: ENotificationSeverity,
  title: React.ReactNode,
  description?: React.ReactNode,
) => void;

export const useNotify = (): NotifyType => {
  const { openNotification } = useContext(notificationContext);

  return useCallback(
    (
      severity: ENotificationSeverity,
      title: React.ReactNode,
      description: React.ReactNode = null,
    ): void => {
      openNotification(DEFAULT_NOTIFICATION_PLACEMENT, {
        severity,
        title,
        description,
      });
    },
    [openNotification],
  );
};

export const withNotification =
  (Component: React.ComponentType) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, react/display-name
  (props: any): React.ReactElement => {
    const { openNotification } = useContext(notificationContext);
    return <Component {...props} openNotification={openNotification} />;
  };
